/**
 * Дашборды ролей tenant-app — по согласованному дизайну manager v3 §3.1
 * «Dashboard — оперативный обзор» (волна 3 «Облака»).
 *
 * Менеджер/владелец: приветствие по времени суток + баннер нарушения SLA +
 * KPI-плитки (активные заявки / нарушен SLA / загрузка номеров % / заезды /
 * выезды / к оплате) + лента последних заявок + быстрые действия. РЕАЛЬНЫЕ
 * данные из бэка:
 *   - GET /v1/admin/requests   — заявки и SLA (useManagerRequests);
 *   - GET /v1/rooms            — загрузка номеров (useRooms);
 *   - GET /v1/rooms/calendar   — заезды/выезды сегодня (useStaysCalendar);
 *   - GET /v1/folios           — выручка/к оплате (useFolios).
 *
 * Плитки скрываются, если бэк не отдал данные (нет фейк-нулей). Мобайл 375:
 * KPI — горизонтальный скролл-ряд (manager v3 phone-фрейм). Полный i18n.
 *
 * Персонал/гость — свои наборы быстрых действий (гость НЕ дёргает
 * менеджерские эндпойнты, RBAC).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformMe } from '../../shared/api/usePlatformMe.js';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { DashboardKind } from '../../shared/nav/resolve-role.js';
import { useManagerRequests } from '../requests-feed/useRequestsFeed.js';
import { useRooms } from '../rooms/useRooms.js';
import { useStaysCalendar } from '../stays/useStays.js';
import { useFolios } from '../billing/useBilling.js';
import { formatMoney, formatDateTime } from '../billing/format.js';
import { categoryLabel, stateLabel, slaInfo } from '../requests-kanban/kanban-model.js';
import { SosTile } from '../sos/SosTile.js';
import {
  DASHBOARD_HEADING,
  activeRequests,
  breachedRequests,
  dayFlow,
  folioTotals,
  greeting,
  isoDay,
  nextDay,
  occupancy,
} from './dashboard-model.js';

const TITLES: Readonly<Record<DashboardKind, string>> = {
  owner: DASHBOARD_HEADING,
  manager: DASHBOARD_HEADING,
  staff: 'Рабочее место персонала',
  guest: 'Личный кабинет гостя',
};

export interface RoleDashboardProps {
  kind: DashboardKind;
  /** Бренд tenant'а для подзаголовка. */
  brand: string;
  /** Имя актора — для приветствия по дизайну («Доброе утро, Имя»). */
  actorName?: string;
}

/** Карточка одного KPI (desktop-сетка + mobile-скролл-пилюля). */
function KpiCard({
  label,
  value,
  hint,
  testId,
  tone,
}: {
  label: string;
  value: number | string;
  hint?: string;
  testId: string;
  tone?: 'alert';
}): JSX.Element {
  return (
    <div className={`ta-kpi${tone === 'alert' ? ' ta-kpi--alert' : ''}`}>
      <p className="ta-kpi__label">{label}</p>
      <p
        className="ta-kpi__value"
        data-testid={testId}
        {...(tone === 'alert' ? { role: 'status' } : {})}
      >
        {value}
      </p>
      {hint ? <p className="ta-kpi__hint">{hint}</p> : null}
    </div>
  );
}

/** Быстрое действие — переход в раздел. */
function QuickAction({
  to,
  label,
  navigate,
  testId,
}: {
  to: string;
  label: string;
  navigate: (to: string) => void;
  testId: string;
}): JSX.Element {
  return (
    <Button variant="secondary" onClick={() => navigate(to)} data-testid={testId}>
      {label}
    </Button>
  );
}

/* ============ Менеджер / Владелец ============ */

function ManagementDashboard({
  kind,
  navigate,
}: {
  kind: 'manager' | 'owner';
  navigate: (to: string) => void;
}): JSX.Element {
  const requests = useManagerRequests();
  const rooms = useRooms({});
  const today = isoDay();
  // Бэк требует to > from — берём окно [сегодня, завтра), фильтруем по дню.
  const calendar = useStaysCalendar(today, nextDay(today));
  const folios = useFolios();

  const items = useMemo(() => requests.data?.items ?? [], [requests.data]);
  const openCount = useMemo(() => activeRequests(items), [items]);
  const breachedCount = useMemo(() => breachedRequests(items), [items]);

  const occ = useMemo(() => occupancy(rooms.data?.items ?? []), [rooms.data]);
  const flow = useMemo(() => dayFlow(calendar.data, today), [calendar.data, today]);
  const folioAgg = useMemo(() => folioTotals(folios.data?.items ?? []), [folios.data]);

  // Лента последних заявок (по дате создания, новые сверху) — лимит 6.
  const recent = useMemo(
    () =>
      [...items]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 6),
    [items],
  );
  // Самая «горящая» просроченная заявка — для баннера SLA по дизайну.
  const topBreach = useMemo(() => {
    const breached = items.filter((r) => slaInfo(r).breached);
    breached.sort(
      (a, b) =>
        (slaInfo(a).minutesLeft ?? 0) - (slaInfo(b).minutesLeft ?? 0),
    );
    return breached[0];
  }, [items]);

  const kpiLoading = requests.isLoading || rooms.isLoading;

  return (
    <>
      {topBreach ? (
        <div className="ta-sla-banner" role="alert" data-testid="dashboard-sla-banner">
          <div>
            <strong>Нарушение норматива реакции (SLA):</strong> «{topBreach.title}» —{' '}
            {slaInfo(topBreach).label}.
          </div>
          <Button
            variant="primary"
            onClick={() => navigate('/requests')}
            data-testid="sla-banner-action"
          >
            К заявкам
          </Button>
        </div>
      ) : null}

      <div className="ta-kpi-row" data-testid="dashboard-kpis">
        {kpiLoading ? (
          Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={92} radius={16} />)
        ) : (
          <>
            <KpiCard
              label="Активные заявки"
              value={openCount}
              testId="kpi-open-requests"
            />
            <KpiCard
              label="Нарушен SLA"
              value={breachedCount}
              testId="kpi-breached-requests"
              {...(breachedCount > 0 ? { tone: 'alert' as const } : {})}
            />
            {occ.total > 0 ? (
              <KpiCard
                label="Загрузка номеров"
                value={`${occ.percent}%`}
                hint={`${occ.occupied} из ${occ.total}`}
                testId="kpi-occupancy"
              />
            ) : null}
            {!calendar.isLoading ? (
              <>
                <KpiCard label="Заезды сегодня" value={flow.arrivals} testId="kpi-arrivals" />
                <KpiCard label="Выезды сегодня" value={flow.departures} testId="kpi-departures" />
              </>
            ) : null}
            {!folios.isError && folioAgg.openFolios > 0 ? (
              <KpiCard
                label="К оплате"
                value={formatMoney(folioAgg.unpaid, folioAgg.currency)}
                hint={`${folioAgg.openFolios} ${pluralFolio(folioAgg.openFolios)}`}
                testId="kpi-unpaid"
              />
            ) : null}
          </>
        )}
      </div>

      <div className="ta-dash-grid">
        <Card>
          <div className="ta-dash-cardhead">
            <h2 className="hsc-card__title">Последние заявки</h2>
            <button
              type="button"
              className="ta-link-btn"
              onClick={() => navigate('/requests')}
              data-testid="recent-see-all"
            >
              Все заявки →
            </button>
          </div>
          {requests.isLoading ? (
            <Skeleton height={180} radius={12} />
          ) : recent.length === 0 ? (
            <p className="ta-muted">Заявок пока нет.</p>
          ) : (
            <ul className="ta-feed" data-testid="dashboard-recent">
              {recent.map((r) => {
                const sla = slaInfo(r);
                return (
                  <li key={r.id} className="ta-feed__item">
                    <div className="ta-feed__main">
                      <span className="ta-feed__title">{r.title}</span>
                      <span className="ta-feed__meta">
                        {categoryLabel(r.category)} · {formatDateTime(r.created_at)}
                      </span>
                    </div>
                    <div className="ta-feed__side">
                      <span className={`ta-chip ta-chip--${chipTone(r.state)}`}>
                        {stateLabel(r.state)}
                      </span>
                      {sla.breached ? (
                        <span className="ta-chip ta-chip--alert">{sla.label}</span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card>
          <h2 className="hsc-card__title">Быстрые действия</h2>
          <div className="hsc-stack" data-testid="dashboard-actions">
            <QuickAction
              to="/requests"
              label="Лента заявок"
              navigate={navigate}
              testId="action-requests"
            />
            <QuickAction to="/rooms" label="Номера" navigate={navigate} testId="action-rooms" />
            <QuickAction
              to="/stays"
              label="Проживания"
              navigate={navigate}
              testId="action-stays"
            />
            <QuickAction
              to="/guest-access"
              label="Гостевой доступ"
              navigate={navigate}
              testId="action-guest-access"
            />
            <QuickAction to="/staff" label="Персонал" navigate={navigate} testId="action-staff" />
            <QuickAction
              to="/schedules"
              label="Графики смен"
              navigate={navigate}
              testId="action-schedules"
            />
            {kind === 'owner' && (
              <QuickAction
                to="/services"
                label="Каталог услуг"
                navigate={navigate}
                testId="action-catalog"
              />
            )}
          </div>
        </Card>
      </div>
    </>
  );
}

/** Тон чипа статуса заявки. */
function chipTone(state: string): string {
  switch (state) {
    case 'resolved':
      return 'done';
    case 'cancelled':
      return 'muted';
    case 'in_progress':
      return 'active';
    default:
      return 'pending';
  }
}

/** Склонение «счёт / счёта / счетов». */
function pluralFolio(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'счёт';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'счёта';
  return 'счетов';
}

/* ============ Персонал ============ */

function StaffDashboard({ navigate }: { navigate: (to: string) => void }): JSX.Element {
  // «Гостевой доступ» — ТОЛЬКО ресепшену (front_desk). Кухня/бар/уборщица
  // (restaurant/housekeeper) этот пункт не видят (Артём 2026-06-18).
  const { data } = usePlatformMe();
  const isFrontDesk = (data?.actor.roles ?? []).includes('front_desk');
  return (
    <Card>
      <h2 className="hsc-card__title">Быстрые действия</h2>
      <div className="hsc-stack" data-testid="dashboard-actions">
        <QuickAction
          to="/requests"
          label="Мои заявки"
          navigate={navigate}
          testId="action-requests"
        />
        <QuickAction
          to="/my-schedule"
          label="Мой график"
          navigate={navigate}
          testId="action-my-schedule"
        />
        {isFrontDesk && (
          <QuickAction
            to="/guest-access"
            label="Гостевой доступ"
            navigate={navigate}
            testId="action-guest-access"
          />
        )}
      </div>
      <p className="ta-muted">
        Здесь — назначенные вам заявки и ближайшие смены. Откройте раздел, чтобы взять заявку в
        работу.
      </p>
    </Card>
  );
}

/* ============ Гость ============ */

/** Внешние ссылки гостевого дома (#79): сразу открываются в новой вкладке. */
const TRANSFER_URL = 'https://welcome-abkhazia.com/transfer#rec554683645';
const EXCURSIONS_URL = 'http://welcome-abkhazia.com/ekskersi#rec554683645';

/** Брендовые иконки плиток главного меню гостя (в нашем стиле, stroke). */
type GuestTileKind = 'stay' | 'menu' | 'in-room' | 'repair' | 'transfer' | 'excursions' | 'other';

function GuestTileIcon({ kind }: { kind: GuestTileKind }): JSX.Element {
  const paths: Record<GuestTileKind, JSX.Element> = {
    stay: (
      <>
        <path d="M3 18v-9m0 5h18v4m0-4V9a2 2 0 0 0-2-2h-8v4" />
        <circle cx="6.5" cy="10.5" r="1.5" />
      </>
    ),
    menu: (
      <>
        <path d="M6 3v18M4 3v5a2 2 0 0 0 4 0V3" />
        <path d="M17 3c-1.4 0-2.3 1.9-2.3 4.5S15.6 12 17 12v9" />
      </>
    ),
    'in-room': (
      <>
        <path d="M4 17h16M5 17a7 7 0 0 1 14 0" />
        <path d="M11 5h2M12 5v2" />
      </>
    ),
    repair: (
      <path d="M14.5 6.5a3.5 3.5 0 0 1-4.6 4.6L4 17l3 3 5.9-5.9a3.5 3.5 0 0 1 4.6-4.6l-2.4 2.4-2-2 2.4-2.4Z" />
    ),
    transfer: (
      <>
        <path d="M4 16v-4l2-4h9l3 4v4" />
        <path d="M3 16h18" />
        <circle cx="7" cy="17.5" r="1.5" />
        <circle cx="17" cy="17.5" r="1.5" />
      </>
    ),
    excursions: (
      <>
        <path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4Z" />
        <path d="M9 4v13M15 6.5v13" />
      </>
    ),
    other: <path d="M21 11.5a8 8 0 0 1-11.7 7.1L3 20.5l1.9-6.3A8 8 0 1 1 21 11.5Z" />,
  };
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[kind]}
    </svg>
  );
}

/** Плитка главного меню гостя — внутренний переход или внешняя ссылка.
 *  Раскладка: иконка слева + название справа (в строке), описание под ними. */
function GuestTile({
  kind,
  name,
  desc,
  testId,
  onClick,
}: {
  kind: GuestTileKind;
  name: string;
  desc: string;
  testId: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" className="cat-card" data-testid={testId} onClick={onClick}>
      <span className="cat-card__head">
        <span className="cat-card__icon" aria-hidden="true">
          <GuestTileIcon kind={kind} />
        </span>
        <span className="cat-card__name">{name}</span>
      </span>
      <span className="cat-card__desc">{desc}</span>
    </button>
  );
}

function GuestDashboard({ navigate }: { navigate: (to: string) => void }): JSX.Element {
  const openExternal = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <>
      {/* SOS (P0, design-guest §15.4) — экстренный вызов на видном месте. */}
      <SosTile />
      <div className="cat-grid" data-testid="guest-home-grid">
        <GuestTile
          kind="stay"
          name="Моё проживание"
          desc="Номер, даты, продление и поздний выезд"
          testId="guest-tile-my-stay"
          onClick={() => navigate('/my-stay')}
        />
        <GuestTile
          kind="menu"
          name="Меню"
          desc="Заказ еды и напитков в номер"
          testId="guest-tile-menu"
          onClick={() => navigate('/menu')}
        />
        <GuestTile
          kind="in-room"
          name="В номер"
          desc="Вода, полотенца, бельё и другое"
          testId="guest-tile-in-room"
          onClick={() => navigate('/in-room')}
        />
        <GuestTile
          kind="repair"
          name="Ремонт"
          desc="Сообщить, что не работает в номере"
          testId="guest-tile-repair"
          onClick={() => navigate('/repair')}
        />
        <GuestTile
          kind="transfer"
          name="Трансфер"
          desc="Заказать трансфер"
          testId="guest-tile-transfer"
          onClick={() => openExternal(TRANSFER_URL)}
        />
        <GuestTile
          kind="excursions"
          name="Экскурсии"
          desc="Экскурсии и морские прогулки"
          testId="guest-tile-excursions"
          onClick={() => openExternal(EXCURSIONS_URL)}
        />
        <GuestTile
          kind="other"
          name="Другое"
          desc="Опишите запрос свободной заявкой"
          testId="guest-tile-other"
          onClick={() => navigate('/other')}
        />
      </div>
    </>
  );
}

export function RoleDashboard({ kind, brand, actorName }: RoleDashboardProps): JSX.Element {
  const navigate = useNavigate();
  const go = (to: string): void => navigate(to);
  const isManagement = kind === 'manager' || kind === 'owner';

  return (
    <section data-testid={`dashboard-${kind}`}>
      {isManagement ? (
        <header className="ta-dash-head">
          <p className="ta-crumbs">Главное · Обзор</p>
          <h1 className="hsc-page__title ta-dash-greeting">
            {greeting()}
            {actorName ? `, ${actorName}` : ''}
          </h1>
          <p className="ta-muted">{brand}</p>
        </header>
      ) : (
        <>
          <h1 className="hsc-page__title">{TITLES[kind]}</h1>
          <p className="ta-muted">{brand}</p>
        </>
      )}
      {isManagement ? (
        <ManagementDashboard kind={kind} navigate={go} />
      ) : kind === 'staff' ? (
        <StaffDashboard navigate={go} />
      ) : (
        <GuestDashboard navigate={go} />
      )}
    </section>
  );
}
